import { useMemo } from 'react';
import { useColorModeValue } from '@chakra-ui/react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Options, SeriesAreaOptions } from 'highcharts';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DateRangeType } from '../../../common/components/DateRangeTypePicker';
import { useChartFormatter } from '../../../common/hooks/use-chart-formatter';
import { getChartTimezoneOffset, getLocaleForDateFns, SupportedLocales } from '../../../utils/common/common-utils';
import { selectSite } from '../../site/siteSlice';
import { CHART_DEFAULT_OPTIONS } from '../constants';
import { getMaxDateTime, getMinDateTime, getUIConfigForRangeType } from '../utils';

interface Props {
  series: SeriesAreaOptions[];
  selectedDateRangeType: DateRangeType;
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>;
  chartOpts: {
    height: number; // in pixels
  };
}

export function useActivityChartData({ series, selectedDateRangeType, rangeTypeToSelectedPeriod, chartOpts }: Props) {
  const { timezone } = useSelector(selectSite);
  const markerColor = useColorModeValue('#000', '#BCBCBC');
  const { i18n, t } = useTranslation();
  const { energyTooltipFormatter, energyValueFormatter } = useChartFormatter(selectedDateRangeType);

  return useMemo<Options>(() => {
    const isDayView = selectedDateRangeType === DateRangeType.Day;
    const { height } = chartOpts;
    const startDate = rangeTypeToSelectedPeriod[selectedDateRangeType];
    const { xAxisTickFormat, xAxisTickInterval } = getUIConfigForRangeType(selectedDateRangeType);
    const localeForDateFns = getLocaleForDateFns(i18n.language as SupportedLocales);

    return {
      ...CHART_DEFAULT_OPTIONS,
      chart: {
        ...CHART_DEFAULT_OPTIONS.chart,
        height: `${height}px`,
      },
      series: _.cloneDeep(
        series.map((s) => ({
          ...s,
          marker: {
            enabled: true,
            radius: 3,
            symbol: 'circle',
          },
          data: isDayView
            ? s.data?.map((point, index) => ({
                ...(point as object),
                marker: {
                  enabled: index % 4 === 0, // data points are every 15min so show points every hour.
                },
              }))
            : s.data,
        }))
      ),
      tooltip: {
        ...CHART_DEFAULT_OPTIONS.tooltip,
        formatter: function () {
          return energyTooltipFormatter(this);
        },
      },
      xAxis: {
        ...CHART_DEFAULT_OPTIONS.xAxis,
        min: getMinDateTime(startDate, selectedDateRangeType, timezone),
        max: getMaxDateTime(startDate, selectedDateRangeType, timezone),
        accessibility: { enabled: true, description: t('Activity.time of the day') },
        tickInterval: xAxisTickInterval,
        labels: {
          style: { fontSize: '11px', color: markerColor },
          formatter: function (data) {
            if (isDayView) {
              return formatInTimeZone(new Date(data.value ?? 0), timezone, xAxisTickFormat);
            } else {
              return format(new Date(data.value ?? 0), xAxisTickFormat, { locale: localeForDateFns });
            }
          },
        },
      },
      yAxis: {
        ...CHART_DEFAULT_OPTIONS.yAxis,
        accessibility: { enabled: true, description: t('Activity.usage in kw') },
        labels: {
          style: { fontSize: '11px', color: markerColor },
          formatter: function (data) {
            return energyValueFormatter(data.value);
          },
        },
      },
      time: {
        getTimezoneOffset: (timestamp: number) => getChartTimezoneOffset(timestamp, timezone),
      },
    };
  }, [
    selectedDateRangeType,
    chartOpts,
    rangeTypeToSelectedPeriod,
    i18n.language,
    series,
    timezone,
    t,
    markerColor,
    energyTooltipFormatter,
    energyValueFormatter,
  ]);
}
